// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE LOAN OFFICER'S FORM FIELDS (Jesse's approval change 2, 2026-10-28): one grid, every field 48px (inputs and
// dropdowns identical — same padding and font), labels on one line, guidance in helper text under the field.
// Labels, options and helper text come from GET /submissions/schema (constants/submissionVocabulary and
// constants/loSubmissionFlow) — this file names no field.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import AddressAutocomplete from '../../AddressAutocomplete';
import './loSubmission.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/** The id a "Fix this" link scrolls to. */
export const targetId = (key) => `lo-target-${String(key).replace(/[^a-zA-Z0-9_-]/g, '-')}`;

export function FieldShell({ fkey, label, help, span, refusal, children, testId }) {
  return (
    <div className={`lo-field${span === 2 ? ' lo-span-2' : span === 'all' ? ' lo-span-all' : ''}`} id={fkey ? targetId(fkey) : undefined}
      data-testid={testId || (fkey ? `lo-field-${fkey}` : undefined)}>
      <span className="lo-label">{label || ' '}</span>
      {children}
      {refusal && <span className="lo-refusal">{refusal}</span>}
      {help && <span className="lo-help">{help}</span>}
    </div>
  );
}

export function TextInput({ meta, value, onChange, span, placeholder, type = 'text', label, readOnly, inputMode, name }) {
  return (
    <FieldShell fkey={meta.key} label={label || meta.label} help={meta.help} span={span} refusal={meta.refusal}>
      <input className="lo-input" type={type} name={name || meta.key} value={value ?? ''} placeholder={placeholder} readOnly={readOnly}
        inputMode={inputMode} onChange={(e) => onChange && onChange(e.target.value)} />
    </FieldShell>
  );
}

const digits = (s) => String(s ?? '').replace(/[^0-9]/g, '');
export function MoneyInput({ meta, value, onChange, span, readOnly, label }) {
  const d = digits(value);
  return (
    <FieldShell fkey={meta.key} label={label || meta.label} help={meta.help} span={span}>
      <input className="lo-input" type="text" inputMode="numeric" name={meta.key} readOnly={readOnly}
        value={d ? `$${Number(d).toLocaleString('en-US')}` : ''} placeholder="$"
        onChange={(e) => onChange && onChange(digits(e.target.value))} />
    </FieldShell>
  );
}

export function SelectInput({ meta, value, onChange, span, options, label, placeholder = 'Select…' }) {
  return (
    <FieldShell fkey={meta.key} label={label || meta.label} help={meta.help} span={span} refusal={meta.refusal}>
      <select className="lo-input" name={meta.key} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">{placeholder}</option>
        {(options || meta.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </FieldShell>
  );
}

/** A two- or three-way choice: buttons of the same 48px. */
export function Segmented({ meta, value, onChange, span, options, label }) {
  return (
    <FieldShell fkey={meta.key} label={label || meta.label} help={meta.help} span={span} refusal={meta.refusal}>
      <div className="lo-seg" role="group" aria-label={label || meta.label}>
        {(options || meta.options || []).map((o) => (
          <button key={o.value} type="button" className="lo-seg-btn" aria-pressed={String(value) === String(o.value) ? 'true' : 'false'}
            data-testid={`lo-choice-${meta.key}-${o.value}`} onClick={() => onChange(o.value)}>{o.label}</button>
        ))}
      </div>
    </FieldShell>
  );
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** Date of birth: month, day and year across one row of the grid, one label. */
export function DobInput({ meta, value, onChange }) {
  const [y, m, d] = String(value || '').split('-');
  const set = (part, v) => {
    const next = { y: y || '', m: m || '', d: d || '', [part]: v };
    onChange(next.y && next.m && next.d ? `${next.y}-${next.m}-${next.d}` : `${next.y || ''}-${next.m || ''}-${next.d || ''}`);
  };
  const thisYear = new Date().getFullYear();
  return (
    <>
      <FieldShell fkey={meta.key} label={meta.label}>
        <select className="lo-input" name="dob_month" aria-label="Month" value={m || ''} onChange={(e) => set('m', e.target.value)}>
          <option value="">Month</option>
          {MONTHS.map((n, i) => <option key={n} value={String(i + 1).padStart(2, '0')}>{n}</option>)}
        </select>
      </FieldShell>
      <FieldShell label="">
        <select className="lo-input" name="dob_day" aria-label="Day" value={d || ''} onChange={(e) => set('d', e.target.value)}>
          <option value="">Day</option>
          {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0')).map((v) => <option key={v} value={v}>{Number(v)}</option>)}
        </select>
      </FieldShell>
      <FieldShell label="">
        <select className="lo-input" name="dob_year" aria-label="Year" value={y || ''} onChange={(e) => set('y', e.target.value)}>
          <option value="">Year</option>
          {Array.from({ length: 90 }, (_, i) => String(thisYear - 18 - i)).map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </FieldShell>
      {meta.help && <span className="lo-help lo-span-all" style={{ marginTop: -8 }}>{meta.help}</span>}
    </>
  );
}

/** An address: street with Places autocomplete; a pick fills city, state and ZIP (AddressParts, the row below). */
export function AddressInput({ meta, values, base, onChange, onPick, span = 'all', label, testId }) {
  const street = values[`${base}street`] || '';
  return (
    <FieldShell fkey={meta.key} label={label || meta.label} span={span} testId={testId}>
      <AddressAutocomplete name={meta.key} value={street} placeholder="Start typing the address…" className="lo-input" style={{}}
        onChange={(e) => onChange(`${base}street`, e.target.value)}
        onSelect={(p) => onPick(base, p)} />
    </FieldShell>
  );
}

/** City, state and ZIP for an address typed by hand (Places unavailable): one row of the grid. */
export function AddressParts({ values, base, onChange }) {
  return (
    <>
      <FieldShell label="City"><input className="lo-input" name={`${base}city`} value={values[`${base}city`] || ''} onChange={(e) => onChange(`${base}city`, e.target.value)} /></FieldShell>
      <FieldShell label="State"><input className="lo-input" name={`${base}state`} maxLength={2} value={values[`${base}state`] || ''} onChange={(e) => onChange(`${base}state`, e.target.value.toUpperCase())} /></FieldShell>
      <FieldShell label="ZIP"><input className="lo-input" name={`${base}zip`} inputMode="numeric" maxLength={5} value={values[`${base}zip`] || ''} onChange={(e) => onChange(`${base}zip`, e.target.value.replace(/\D/g, ''))} /></FieldShell>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE ZIP QUESTION, ASKED THE WAY /apply ASKS IT: several ZIPs, each checked against GET /api/zip/check/:zip as it
// is added. The verdict is shown, never stored; an unreachable check is "could not check", never a refusal.
// ─────────────────────────────────────────────────────────────────────────────
export function ZipField({ meta, value, onChange, span = 'all' }) {
  const zips = value ? String(value).split(',').map((z) => z.trim()).filter(Boolean) : [];
  const [draft, setDraft] = React.useState('');
  const [verdicts, setVerdicts] = React.useState({});
  const inFlight = React.useRef(new Set());
  const check = React.useCallback((z) => {
    if (!/^\d{5}$/.test(z) || inFlight.current.has(z)) return;
    inFlight.current.add(z);
    fetch(`${API_URL}/api/zip/check/${z}`).then((r) => r.json())
      .then((d) => setVerdicts((p) => ({ ...p, [z]: d })))
      .catch(() => setVerdicts((p) => ({ ...p, [z]: { confirmable: false } })))
      .finally(() => inFlight.current.delete(z));
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { zips.forEach(check); }, [value, check]);
  const add = (raw) => {
    const z = String(raw).replace(/\D/g, '').slice(0, 5);
    if (z.length !== 5 || zips.includes(z)) { setDraft(''); return; }
    onChange([...zips, z].join(', ')); setDraft('');
  };
  const badge = (z) => {
    const v = verdicts[z];
    if (!v) return { text: 'checking…', bg: '#f3f4f6', fg: '#6b7280' };
    if (v.reason === 'state_not_served') return { text: `${v.state || 'that state'} — not served`, bg: '#fde8e4', fg: '#b3321f' };
    if (v.confirmable === false) return { text: 'could not check', bg: '#fffbeb', fg: '#92400e' };
    if (v.eligible) return { text: 'served', bg: 'var(--color-success-light)', fg: 'var(--color-success-text)' };
    return { text: v.reason === 'outside_service_area' ? 'outside the service area' : 'not served', bg: '#fde8e4', fg: '#b3321f' };
  };
  return (
    <FieldShell fkey={meta.key} label={meta.label} help={meta.help} span={span}>
      <input className="lo-input" type="text" inputMode="numeric" name={meta.key} value={draft} placeholder="78701"
        onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 5); setDraft(v); if (v.length === 5) add(v); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(draft); } }}
        onBlur={() => add(draft)} />
      {zips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {zips.map((z) => {
            const b = badge(z);
            return (
              <span key={z} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 8px 5px 10px', borderRadius: 8,
                background: b.bg, color: b.fg, fontSize: 12.5, fontWeight: 700 }}>
                {z}<span style={{ fontWeight: 500 }}>· {b.text}</span>
                <button type="button" aria-label={`Remove ${z}`} onClick={() => onChange(zips.filter((x) => x !== z).join(', '))}
                  style={{ border: 'none', background: 'none', color: b.fg, cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '0 2px' }}>×</button>
              </span>
            );
          })}
        </div>
      )}
    </FieldShell>
  );
}
